/**
 * An ad network's screens (Ad Network Intelligence, docs/pdlc/ani-screen-lists.md).
 * PURE; runs in the browser (the preview) and on the server (the save).
 *
 * A screen is a placement the network names: latitude and longitude, the
 * network's own screen id, and where it is (address, city, state, zip), its
 * format and its venue. A row without a screen id is refused: DANI keys every
 * screen by it, and a new version tells unchanged from new by it. A screen id
 * named twice keeps its first row.
 */
import type { SavedPlacement, SavedScreen } from '../contracts/pql';
import { coordinateColumns, headerRowOf, radiusForType, RADIUS_HEADERS } from '../ooh/placements';
import {
    ADDRESS_HEADERS,
    CITY_HEADERS,
    colOf,
    FORMAT_HEADERS,
    SCREEN_ID_HEADERS,
    STATE_HEADERS,
    VENUE_HEADERS,
    ZIP_HEADERS,
} from './columns';

/** A header as an attribute key: "Business Type" → business_type. */
export function attributeKey(header: string): string {
    return header
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 60);
}

const MAX_ATTRIBUTE_CHARS = 500;

/**
 * Most screens in one saved screen list. An ad network's list is its whole
 * network (one network sent 19,100 screens, 2026-10-28), kept for its Ad
 * Network Intelligence report and published to DANI in batches, so it is not
 * held to a check's 5,000 locations (MAX_OOH_PLACEMENTS): a check that uses the
 * list is still held to that by its own request.
 */
export const MAX_NETWORK_SCREENS = 50_000;

/**
 * Every filled column of a row but latitude and longitude, keyed by its header
 * (the network's own names, so DANI reads them as it reads the rows already
 * loaded). Whole numbers and decimals stay numbers.
 */
export function attributesOf(
    headers: string[],
    row: string[],
    skip: number[],
): Record<string, string | number> {
    const out: Record<string, string | number> = {};
    headers.forEach((h, i) => {
        if (skip.includes(i)) return;
        const key = attributeKey(String(h ?? ''));
        const value = String(row[i] ?? '').trim();
        if (!key || !value || key in out) return;
        out[key] =
            /^-?\d+(\.\d+)?$/.test(value) && value.length < 16
                ? Number(value)
                : value.slice(0, MAX_ATTRIBUTE_CHARS);
    });
    return out;
}

export interface ParsedScreens {
    placements: SavedPlacement[];
    screens: SavedScreen[];
    refusals: string[];
    duplicates: number;
}

/** True when a location table has a screen id column. */
export function hasScreenIdColumn(rows: string[][]): boolean {
    const h = headerRowOf(rows);
    return h >= 0 && colOf(rows[h], SCREEN_ID_HEADERS) >= 0;
}

export function parseScreenRows(rows: string[][]): ParsedScreens {
    const h = headerRowOf(rows);
    if (h < 0)
        return {
            placements: [],
            screens: [],
            refusals: [
                'The file needs a latitude column (lat or latitude) and a longitude column (lng, lon or longitude).',
            ],
            duplicates: 0,
        };
    const headers = rows[h].map((c) => String(c ?? ''));
    const { lat: latCol, lng: lngCol } = coordinateColumns(rows, h);
    const idCol = colOf(headers, SCREEN_ID_HEADERS);
    if (idCol < 0)
        return {
            placements: [],
            screens: [],
            refusals: [
                'The file needs a screen id column (Screen ID, Geopath ID or Unit ID): DANI keeps each screen by it.',
            ],
            duplicates: 0,
        };
    const at = (names: string[]) => colOf(headers, names);
    const cols = {
        address: at(ADDRESS_HEADERS),
        city: at(CITY_HEADERS),
        state: at(STATE_HEADERS),
        zip: at(ZIP_HEADERS),
        format: at(FORMAT_HEADERS),
        venue: at(VENUE_HEADERS),
        radius: at(RADIUS_HEADERS),
    };
    const placements: SavedPlacement[] = [];
    const screens: SavedScreen[] = [];
    const refusals: string[] = [];
    const seen = new Set<string>();
    let duplicates = 0;
    rows.slice(h + 1).forEach((row, i) => {
        const rowNo = h + i + 2;
        const cell = (c: number) => (c >= 0 ? String(row[c] ?? '').trim() : '');
        if (row.every((c) => !String(c ?? '').trim())) return;
        const screenId = cell(idCol);
        if (!screenId) {
            refusals.push(`Row ${rowNo}: no screen id.`);
            return;
        }
        const lat = Number(cell(latCol));
        const lng = Number(cell(lngCol));
        if (
            !cell(latCol) ||
            !cell(lngCol) ||
            !Number.isFinite(lat) ||
            !Number.isFinite(lng) ||
            lat < 17 ||
            lat > 72 ||
            lng < -180 ||
            lng > -60
        ) {
            refusals.push(`Row ${rowNo}: latitude and longitude must be a point in the US.`);
            return;
        }
        if (seen.has(screenId)) {
            duplicates++;
            return;
        }
        seen.add(screenId);
        const format = cell(cols.format) || undefined;
        const stated = Number(cell(cols.radius));
        const radiusMeters =
            Number.isFinite(stated) && stated >= 10 && stated <= 1000
                ? Math.round(stated)
                : radiusForType(format ?? cell(cols.venue));
        placements.push({
            lat,
            lng,
            radiusMeters,
            ...(format ? { type: format.slice(0, 80) } : {}),
        });
        const opt = (v: string, max: number) => (v ? v.slice(0, max) : undefined);
        const attributes = attributesOf(headers, row, [latCol, lngCol]);
        screens.push({
            screenId: screenId.slice(0, 80),
            address: opt(cell(cols.address), 200),
            city: opt(cell(cols.city), 80),
            state: opt(cell(cols.state), 40),
            zip: opt(cell(cols.zip).replace(/-\d{4}$/, ''), 10),
            format: opt(format ?? '', 80),
            venue: opt(cell(cols.venue), 160),
            ...(Object.keys(attributes).length ? { attributes } : {}),
        });
    });
    if (placements.length > MAX_NETWORK_SCREENS)
        return {
            placements: [],
            screens: [],
            refusals: [
                `${placements.length.toLocaleString('en-US')} screens is more than one file takes (${MAX_NETWORK_SCREENS.toLocaleString('en-US')}). Split it by market.`,
            ],
            duplicates,
        };
    return { placements, screens, refusals, duplicates };
}

/** A network's own screen id column, as its file names it (columns.ts). */
const NETWORK_BY_ID_HEADER: readonly (readonly [readonly string[], string])[] = [
    [['venue id'], 'Bulletin Screen'],
    [['gtvid', 'gtv id'], 'GSTV'],
    [['unique id', 'geopath panel id'], 'Outfront'],
];
/** The network in a file's name ("Bulletin Screen List 10_26.csv"). */
const NETWORK_BY_NAME: readonly (readonly [RegExp, string])[] = [
    [/bulletin/i, 'Bulletin Screen'],
    [/gstv/i, 'GSTV'],
    [/outfront/i, 'Outfront'],
    [/grocery\s*tv/i, 'GroceryTV'],
];

/**
 * Which network a screen file is, so nobody picks what the file already says
 * (2026-10-28): its own screen id column first (Bulletin's Venue ID, GSTV's GTVID,
 * Outfront's Unique ID), then its name. Null when neither says; the person picks.
 */
export function guessNetwork(
    fileName: string,
    sheets: readonly { rows: string[][] }[],
): string | null {
    for (const sheet of sheets) {
        const h = headerRowOf(sheet.rows);
        if (h < 0) continue;
        const headers = sheet.rows[h].map((c) =>
            String(c ?? '')
                .toLowerCase()
                .replace(/[_-]+/g, ' ')
                .trim(),
        );
        for (const [names, network] of NETWORK_BY_ID_HEADER)
            if (headers.some((x) => names.includes(x))) return network;
    }
    return NETWORK_BY_NAME.find(([re]) => re.test(fileName))?.[1] ?? null;
}
